/**
 * Maps every documented Remediation API error code to a customer-readable
 * one-sentence string. Falls back to the API's detail field if a code isn't
 * mapped, and a generic message if neither exists.
 *
 * Pulled directly from UI_INTEGRATION_GUIDE.md §Cross-cutting response shapes.
 */

/** FastAPI / Pydantic validation errors: { detail: [{ loc, msg, type }, ...] } */
export const formatValidationDetailList = (detail) => {
  if (!Array.isArray(detail)) return [];
  return detail.map((item) => {
    if (typeof item === "string") return item;
    const fieldParts = (item.loc || []).filter((p) => p !== "body" && p !== "query");
    const field = fieldParts.join(".").replace(/_/g, " ");
    if (item.type === "missing") {
      return field ? `${field} is required.` : item.msg || "A required field is missing.";
    }
    if (item.type === "extra_forbidden") {
      return field
        ? `"${field}" is not accepted — remove it and try again.`
        : item.msg || "The request included an unexpected field.";
    }
    if (item.msg) return field ? `${field}: ${item.msg}` : item.msg;
    return field ? `${field} is invalid.` : "A field is invalid.";
  });
};

const formatLocalDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return String(iso);
  }
};

/** Prefer API-provided customer copy when present (Remediation Hub contract). */
export const pickCustomerMessage = (err) => {
  const msg = err?.customer_message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return null;
};

/** Snackbar options for API / validation failures (422 detail arrays, etc.). */
export const apiErrorSnackbarOpts = (err, fallback = "Something went wrong.") => {
  const customer = pickCustomerMessage(err);
  if (customer) {
    return { message: customer, severity: "error" };
  }
  const details = err?.validationDetails;
  if (Array.isArray(details) && details.length > 0) {
    if (details.length === 1) {
      return { message: details[0], severity: "error" };
    }
    return {
      message: "Please correct the following:",
      severity: "error",
      details,
    };
  }
  return { message: friendlyErrorMessage(err) || fallback, severity: "error" };
};

export const friendlyErrorMessage = (err) => {
  if (!err) return "Something went wrong.";
  if (typeof err === "string") return err;

  const customer = pickCustomerMessage(err);
  if (customer) return customer;

  if (Array.isArray(err.validationDetails) && err.validationDetails.length > 0) {
    return err.validationDetails.join(" ");
  }
  if (Array.isArray(err.detail)) {
    const lines = formatValidationDetailList(err.detail);
    if (lines.length) return lines.join(" ");
  }

  const code = err.error || err.code;
  const detail = err.detail || err.message;
  const ctx = err.context || {};

  switch (code) {
    case "missing-scope":
      return "We couldn't determine your organization. Please pick one from the top-right switcher and try again.";
    case "unknown-org":
      return "We can't find that organization in your account.";
    case "org-access-denied":
      return "You don't have access to that organization.";
    case "not-authorized-to-auto-approve":
      return "Only Administrators can use Auto-approve.";
    case "not-authorized":
      return "You don't have permission to do that.";
    case "invalid_scheduled_at": {
      if (ctx.reason === "too_soon" && ctx.min_scheduled_at) {
        return `Pick a time at or after ${formatLocalDateTime(ctx.min_scheduled_at)}.`;
      }
      if (ctx.reason === "too_far_out" && ctx.max_scheduled_at) {
        return `Pick a time at or before ${formatLocalDateTime(ctx.max_scheduled_at)} (max 30 days out).`;
      }
      return "That scheduled time isn't allowed.";
    }
    case "invalid_cursor":
      return "Your view is out of date — please reset filters and try again.";
    case "validation":
      return detail || "Some fields are invalid. Please check and try again.";
    case "not_found":
      return "We couldn't find that request.";
    default:
      return detail || "Something went wrong.";
  }
};

export const friendlySkippedReason = (skipped) => {
  if (!skipped) return "Skipped.";
  if (skipped.customer_message) return skipped.customer_message;
  const { reason, context = {} } = skipped;
  switch (reason) {
    case "wrong_status":
      return `This request is in ${context.current_status || "an incompatible status"} — that action isn't available right now.`;
    case "not_found":
      return "Request not found in this organization.";
    case "already_terminal":
      return "Already finished — no action needed.";
    case "cancel_window_closed":
      return "Too late to cancel — execution starts within 30 minutes.";
    case "already_due":
      return "Already due — it'll run on the next worker tick.";
    case "stale_preview":
      return "Preview is more than 24 hours old — please refresh before approving.";
    case "check_not_remediable":
      return "This check isn't available for one-click remediation right now.";
    case "invalid_scheduled_at":
      return friendlyErrorMessage({ error: "invalid_scheduled_at", context });
    default:
      return reason ? reason.replace(/_/g, " ") : "Skipped.";
  }
};

/** Reasons that are user-fault and should render as warnings, not errors. */
export const isUserFixableSkip = (reason) =>
  ["cancel_window_closed", "already_due", "stale_preview", "wrong_status"].includes(reason);
